import { useState } from 'react';
import { AdminAPI } from '@shared/types/api';
import { useEnqueueAgentRun } from '../../api/useAdminOps';
import {
  NEIGHBORHOOD_STAGE_TASKS,
  PIP_STYLES,
  STAGE_SHORT,
  stageStatusLabel,
  timeAgo,
  timeUntil,
} from './opsUi';

// The AI pipeline for one (event × neighborhood) as clickable stage buttons:
// each button shows the stage's run status and enqueues that exact task for
// this scope when clicked. If a run is already active for the scope, the
// server debounces (sets rerun_requested) and says so in the returned message.

export default function StageRunButtons({
  eventTimeId,
  neighborhoodId,
  milestones,
}: {
  eventTimeId: string;
  neighborhoodId: string;
  milestones: AdminAPI.OpsMilestone[];
}) {
  const enqueue = useEnqueueAgentRun();
  const [firedTask, setFiredTask] = useState<string | null>(null);

  const stages = milestones.filter((m) =>
    (NEIGHBORHOOD_STAGE_TASKS as string[]).includes(m.task)
  );

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1 flex-wrap">
        {stages.map((m) => (
          <button
            key={m.task}
            onClick={(e) => {
              e.stopPropagation();
              setFiredTask(m.task);
              enqueue.mutate({
                taskType: m.task as AdminAPI.AgentTaskType,
                eventTimeId,
                neighborhoodId,
              });
            }}
            disabled={enqueue.isPending}
            title={`${m.label}: ${stageStatusLabel(m.status)} (${
              new Date(m.plannedAt) > new Date() ? timeUntil(m.plannedAt) : timeAgo(m.plannedAt)
            }) — click to run now for this neighborhood`}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-neutral-200 dark:border-neutral-700 text-[10px] font-medium text-dark-muted hover:border-primary-400 hover:text-primary-500 disabled:opacity-50"
          >
            <span className={`w-2 h-2 rounded-[3px] ${PIP_STYLES[m.status] ?? PIP_STYLES.pending}`} />
            {STAGE_SHORT[m.task] ?? m.task}
          </button>
        ))}
      </div>
      {firedTask && enqueue.data?.message && (
        <p className="text-[10px] text-dark-muted">
          {STAGE_SHORT[firedTask] ?? firedTask}: {enqueue.data.message}
        </p>
      )}
      {enqueue.isError && (
        <p className="text-[10px] text-red-600">
          Failed to queue run: {(enqueue.error as Error)?.message ?? 'unknown error'}
        </p>
      )}
    </div>
  );
}
